import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/videos";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catWrapRef.current && !catWrapRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/search?q=${encodeURIComponent(kw)}`);
    setKeyword("");
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl">
              <i className="ri-play-fill"></i>
            </span>
            <span className="text-lg font-heading font-bold text-foreground-950 tracking-tight">
              糖豆<span className="text-primary-600">视频</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 text-sm rounded-md transition-colors ${
                isActive("/") ? "text-primary-700 font-medium" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              首页
            </Link>

            <div className="relative" ref={catWrapRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`px-3 py-2 text-sm rounded-md flex items-center gap-1 transition-colors cursor-pointer ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-700 font-medium"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-[420px] p-3 grid grid-cols-2 gap-1 bg-background-50 border border-background-200 rounded-lg animate-scale-in">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className={`px-3 py-2 text-sm rounded-md flex items-center gap-1 transition-colors cursor-pointer ${
                searchOpen ? "text-primary-700 font-medium" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              搜索
            </button>

            {navLinks
              .filter((l) => l.to !== "/")
              .map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive(link.to)
                      ? "text-primary-700 font-medium"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {link.label}
                </Link>
              ))}

            <Link
              to="/search"
              className="ml-2 px-4 py-2 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              开始观看
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen((v) => !v)}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="pb-4 animate-fade-in">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm"></i>
                </span>
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索视频名称、作者或分类…"
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        )}

        {menuOpen && (
          <div className="md:hidden pb-4 animate-fade-in">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="px-3 py-2.5 text-sm rounded-md text-foreground-800 hover:bg-background-100"
              >
                首页
              </Link>
              <div className="px-3 pt-2 pb-1 text-xs font-medium text-foreground-400">分类</div>
              <div className="grid grid-cols-2 gap-1 px-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <i className={`${cat.icon} text-primary-600`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
              <Link
                to="/search"
                className="px-3 py-2.5 text-sm rounded-md text-foreground-800 hover:bg-background-100"
              >
                搜索
              </Link>
              <Link
                to="/about"
                className="px-3 py-2.5 text-sm rounded-md text-foreground-800 hover:bg-background-100"
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}